import React, { useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Modal } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons'
import UserContext from 'context/user-context'
import AppBadge from 'shared/badges/app-badge'
import { useAnalytics } from 'api/hooks/use-analytics'
import { parseFilterTree, requestFilterTree } from 'api/filter-tree'
import FilterGroup from './FilterGroup'
import AppliedFilterList from './AppliedFilterList'
import { appliedViewFilters } from './applied-filters'
import { buildFilterColumns } from './filter-columns'
import { countFilters, startingTree } from './tree'

// The View's AND/OR filter groups, ANDed with its column filters by the API.
const FilterGroups = ({ params, setParams, columns = [], customColumns = [], canEdit = true, dashboardId }) => {
  const { municipality } = useContext(UserContext)
  const [show, setShow] = useState(false)
  const [draft, setDraft] = useState(null)

  const { data: analytics } = useAnalytics({
    params: { section_id: params.section_id, perPage: 100 },
    options: { enabled: show && !!params.section_id }
  })

  const filterColumns = useMemo(
    () => buildFilterColumns({
      columns, customColumns, analytics: analytics?.data || [], municipality, sectionId: params.section_id
    }),
    [columns, customColumns, analytics, municipality, params.section_id]
  )

  const activeCount = countFilters(requestFilterTree(parseFilterTree(params.filterTree)))
  const appliedFilters = show ? appliedViewFilters({ params, columns, customColumns, municipality }) : []

  const open = () => {
    setDraft(startingTree(params.filterTree))
    setShow(true)
  }

  const apply = () => {
    setParams((current) => ({ ...current, filterTree: requestFilterTree(draft) ? draft : null }))
    setShow(false)
  }

  return (
    <>
      <Button
        variant={activeCount ? 'primary' : 'light'}
        className={activeCount ? '' : 'text-primary'}
        size="sm"
        onClick={open}
        data-testid="filter-groups-open"
      >
        <FontAwesomeIcon icon={faLayerGroup} className="me-1" />
        Filter groups
        {activeCount > 0 && (
          <AppBadge pill className="ms-1 bg-white text-primary" data-testid="filter-groups-count">
            {activeCount}
          </AppBadge>
        )}
      </Button>

      {/* The filter editors open in popovers portalled outside the modal; a focus trap would
          pull focus back out of their inputs. */}
      <Modal show={show} onHide={() => setShow(false)} size="lg" enforceFocus={false}>
        <Modal.Header closeButton>
          <Modal.Title className="lead fw-normal">Filter groups</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3" data-testid="filter-groups-applied">
            <div className="fw-bold small">Column filters</div>
            <div className="text-muted small mb-2">Applied in addition to the groups below. Edit from the column headers.</div>
            <AppliedFilterList appliedFilters={appliedFilters} emptyText="No column filters applied." />
          </div>
          <hr />
          <div className="fw-bold small">Filter groups</div>
          <p className="text-muted small">
            Use AND to require every filter, or OR to allow any of them. Add a group to combine filters before joining them with the rest.
          </p>
          {draft && (
            <FilterGroup
              group={draft}
              tree={draft}
              setTree={setDraft}
              isRoot
              canEdit={canEdit}
              filterColumns={filterColumns}
              dashboardId={dashboardId}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          {canEdit && (
            <Button
              variant="light"
              className="text-primary me-auto"
              onClick={() => setDraft(startingTree(null))}
              data-testid="filter-groups-clear"
            >
              Clear all
            </Button>
          )}
          <Button variant="light" className="text-primary" onClick={() => setShow(false)}>
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button variant="primary" onClick={apply} data-testid="filter-groups-apply">
              Apply
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}

FilterGroups.propTypes = {
  params: PropTypes.object.isRequired,
  setParams: PropTypes.func.isRequired,
  columns: PropTypes.array,
  customColumns: PropTypes.array,
  canEdit: PropTypes.bool,
  dashboardId: PropTypes.string
}

export default FilterGroups
